import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { motion, AnimatePresence } from 'framer-motion';
import { getUserById } from '../../../api/user';
import { getPointById } from '../../../api/point';
import { errorMessages } from '../../../utils/methods';
import { MEMBER_ADDITION_INFO_KEY } from '../../../constants/queryKeys';
import UserGeneralTab from '../UserGeneralTab/UserGeneralTab';
import AttendanceReservation from '../attendanceReservation/AttendanceReservation';
import UserTab3 from '../coursTicketDetail/CoursTicketDetail';
import UserPaymentHistory from '../UserPaymentHistory/UserPaymentHistory';
import UserTransfer from '../UserTransfer/UserTransfer';
import ConsultationHistory from '../ConsultationHistory/ConsultationHistory';
import UserCourseHistory from '../UserCourseHistory/UserCourseHistory';
import UserContractHistory from '../UserContractHistory/UserContractHistory';
import UserPointHistory from '../UserPointHistory/UserPointHistory';
import EnterSaleInfoModal from '../../SalesManagment/modal/enterSaleInfoModal/EnterSaleInfoModal';
import UserDetailTabs from './UserDetailTabs';
import UserDetailUserInfo from './UserDetailUserInfo';
import CommonLoading from '../../../form-components/CommonLoading';
import LightVersionOverlay, { useTransientLightVersionOverlay } from '../../common/LightVersionOverlay';
import { PageContainer, DividerSmall } from './UserDetailStyled';

const DEFAULT_TAB = 'one';

// 회원이 속한 지점 목록을 로그인 관리자 권한 기준으로 필터링한다.
// SUPERADMIN: 전체 노출, HEAD centerType 관리자: 본사 ID 매칭 지점만, 그 외: 본인 지점만.
const applyBranchScope = (userData, bigUser) => {
  if (!userData || bigUser?.userRole === 'ROLE_SUPERADMIN') return userData;

  const isHeadAdmin = bigUser?.centerType === 'HEAD';
  const headBranchId = bigUser?.headBranch?.headBranchId;
  const branchId = bigUser?.branch?.branchId;

  const scoped = (userData.memberBranchList || []).filter(branch =>
    isHeadAdmin
      ? branch.centerId === headBranchId || branch.headCenterId === headBranchId
      : branch.branchId === branchId,
  );

  // 소속 지점이 비는 회원(다지점 공유 상품(#73)으로 타 지점 회원이 잡히거나, 본인 상품 없이
  // 공유받은 수강권만 있는 회원)은 위 필터가 비어 지점이 정해지지 않고,
  // 그러면 상세가 무한 로딩에 걸린다(userInfo 조회가 userBranchId 에 enabled 돼 있다).
  // 본사 관리자도 마찬가지라 계정 유형과 무관하게 조회 지점으로 채운다.
  // 로그인 지점으로 채워 화면을 열고, 조회는 그 지점 기준이라 결제·포인트는
  // 비어 보이는 게 맞다(지점별 격리).
  if (scoped.length === 0 && branchId) {
    scoped.push({
      branchId,
      branchName: bigUser?.branch?.branchName ?? '',
      headCenterId: null,
      centerId: null,
    });
  }

  return {
    ...userData,
    memberBranchList: [...scoped].sort((a, b) => a.branchId - b.branchId),
  };
};

const UserDetailContent = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const bigUser = useSelector(state => state.user.user);
  const toast = useToast();
  const isGuestUser = bigUser?.userRole === 'ROLE_GUEST';
  const liteOverlay = useTransientLightVersionOverlay();

  const initialTab = searchParams.get('tab') || DEFAULT_TAB;
  const initialSubtab = parseInt(searchParams.get('subtab') || '0', 10);
  // ?ticketId= : 상품 상세 탭에서 이 회원 수강권 카드로 스크롤·강조(스케줄 확인 창의 회원 이름 링크가 넘긴다).
  const focusTicketId = searchParams.get('ticketId');
  const [selectedTab, setSelectedTab] = useState(initialTab);

  // 탭 변경 시 URL의 ?tab= 동기화 — 새로고침 후에도 같은 탭이 유지되도록.
  // replace=true: 탭 클릭이 브라우저 히스토리에 쌓이지 않음 (뒤로가기 동작 자연스러움 유지).
  useEffect(() => {
    if (searchParams.get('tab') !== selectedTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', selectedTab);
      setSearchParams(next, { replace: true });
    }
  }, [selectedTab]);
  const [saleModalOpen, setSaleModalOpen] = useState(false);
  // 라커 카드 [연장] 진입 시 판매등록 모달에 넘길 자리·기간 프리셋
  const [saleLockerExtension, setSaleLockerExtension] = useState(null);

  // 헤더 카드 + General Tab이 공유하는 회원 이용 정보 (선택된 지점 기준).
  const [userBranchId, setUserBranchId] = useState();
  const [userInfo, setUserInfo] = useState(null);

  const userTab3Ref = useRef();
  const paymentHistoryRef = useRef();

  // 회원 목록 페이지가 "회원 상세에서 돌아왔는지" 알 수 있도록 언마운트 시 플래그를 남긴다.
  // (MemberIndex가 fromMemberDetail 값으로 검색조건 복원 여부를 판단)
  useEffect(() => {
    return () => {
      localStorage.setItem('fromMemberDetail', 'true');
    };
  }, []);

  const { data: rawUser } = useQuery({
    queryKey: ['user-detail', id],
    queryFn: () => getUserById(id, bigUser.token),
    enabled: !!id && !!bigUser?.token,
  });

  const user = useMemo(() => applyBranchScope(rawUser, bigUser), [rawUser, bigUser]);

  // 회원 데이터가 들어오면 기본 지점을 선택한다.
  // 관리자가 현재 보고 있는 지점을 우선하고(다지점 회원이 ID 낮은 지점으로 튀는 문제 방지),
  // 회원이 그 지점 소속이 아니면 목록 첫 번째로 폴백.
  useEffect(() => {
    if (user?.memberBranchList && user.memberBranchList.length > 0 && !userBranchId) {
      const currentBranchId = bigUser?.branch?.branchId;
      const preferred = user.memberBranchList.find(branch => branch.branchId === currentBranchId);
      setUserBranchId(preferred ? preferred.branchId : user.memberBranchList[0].branchId);
    }
  }, [user, userBranchId, bigUser]);

  // 선택된 지점 기준으로 회원 이용 정보 조회.
  // React Query로 캐싱해 포인트 적립/차감·메모 저장 후 invalidate만으로 헤더가 갱신되게 한다.
  const {
    data: fetchedUserInfo,
    isError: userInfoError,
    error: userInfoErrorObject,
  } = useQuery({
    queryKey: MEMBER_ADDITION_INFO_KEY(user?.memberId, userBranchId),
    queryFn: () => getPointById(user.memberId, bigUser.token, userBranchId),
    enabled: !!user?.memberId && !!userBranchId && !!bigUser?.token,
  });

  useEffect(() => {
    if (fetchedUserInfo) setUserInfo(fetchedUserInfo);
  }, [fetchedUserInfo]);

  useEffect(() => {
    if (!userInfoError) return;
    toast.error(errorMessages(userInfoErrorObject));
    // 에러 시 로딩 게이트가 풀리도록 빈 객체로 폴백 (아래 `user && userInfo` 조건이 통과되어야 함).
    setUserInfo({});
  }, [userInfoError, userInfoErrorObject]);

  const handleTabChange = (_, newValue) => setSelectedTab(newValue);

  const handleSaleModalFetchData = async () => {
    if (userTab3Ref.current) await userTab3Ref.current.refreshData();
    if (paymentHistoryRef.current) await paymentHistoryRef.current.refreshData();
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'one':
        return (
          <UserGeneralTab
            setSelectedTab={setSelectedTab}
            user={user}
            bigUser={bigUser}
            userInfo={userInfo}
            userBranchId={userBranchId}
            setUserBranchId={setUserBranchId}
            key={bigUser}
          />
        );
      case 'two':
        return <AttendanceReservation subtab={initialSubtab} user={user} bigUser={bigUser} />;
      case 'three':
        return (
          <UserTab3
            ref={userTab3Ref}
            user={user}
            bigUser={bigUser}
            focusTicketId={focusTicketId}
            onLockerExtend={item => {
              // 카드의 자리 정보로 판매등록(라커 탭)을 연다. expireDate 는 기간 프리셋용.
              setSaleLockerExtension({
                lockerId: item.branchLockerId,
                lockerNumber: item.lockerNumber,
                lockerAreaName: item.lockerAreaName,
                expireDate: item.expireDate,
              });
              setSaleModalOpen(true);
            }}
          />
        );
      case 'four':
        return (
          <UserPaymentHistory
            id={user.memberId}
            ref={paymentHistoryRef}
            refreshData={async () => {
              if (paymentHistoryRef.current) await paymentHistoryRef.current.refreshData();
            }}
          />
        );
      case 'five':
        return <UserTransfer user={user} bigUser={bigUser} />;
      case 'six':
        return <ConsultationHistory member={user} bigUser={bigUser} />;
      case 'eight':
        return <UserCourseHistory user={user} bigUser={bigUser} />;
      case 'ten':
        return <UserPointHistory user={user} bigUser={bigUser} />;
      case 'nine':
        return <UserContractHistory user={user} bigUser={bigUser} />;
      // 'seven' (구 일반상품 상세)은 'three'(보유 상품)에 통합됨 — 호환을 위해 같은 화면으로 폴백.
      case 'seven':
        return <UserTab3 ref={userTab3Ref} user={user} bigUser={bigUser} />;
      default:
        return <UserGeneralTab setSelectedTab={setSelectedTab} user={user} bigUser={bigUser} key={bigUser} />;
    }
  };

  return (
    <LightVersionOverlay active={liteOverlay.active}>
    <PageContainer>
      <UserDetailTabs
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        isGuestUser={isGuestUser}
        onLiteBlockedTabClick={liteOverlay.show}
      />
      <DividerSmall />

      {/* 회원 기본정보(user) + 이용정보(userInfo) 둘 다 도착해야 본문 렌더 — */}
      {/* 한쪽만 있을 때 자식이 빈 값으로 렌더되어 UI가 깜빡이는 것을 막는다. */}
      {user && userInfo ? (
        <>
          <UserDetailUserInfo
            user={user}
            userInfo={userInfo}
            userBranchId={userBranchId}
            token={bigUser?.token}
            onSaleClick={() => setSaleModalOpen(true)}
          />
          <EnterSaleInfoModal
            userInfo={{ userId: id, userType: 'member' }}
            userDetail={user}
            open={saleModalOpen}
            onOpen={setSaleModalOpen}
            onClose={() => {
              setSaleModalOpen(false);
              setSaleLockerExtension(null);
            }}
            initialLockerExtension={saleLockerExtension}
            hideButton={true}
            fetchData={handleSaleModalFetchData}
          />

          {/* 탭 전환 시 짧은 페이드. mode="wait" 없이 두면 연타 시에도 자연스럽게 cross-fade. */}
          <AnimatePresence>
            <motion.div
              key={selectedTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}>
              {renderTabContent()}
            </motion.div>
          </AnimatePresence>
        </>
      ) : (
        <CommonLoading />
      )}
    </PageContainer>
    </LightVersionOverlay>
  );
};

// 공유 회원 이름 링크로 다른 회원 상세로 이동해도 같은 라우트라 컴포넌트가 재사용된다.
// 선택 지점·탭 등 이전 회원 상태가 남지 않도록 회원 ID 를 key 로 걸어 새로 마운트한다.
const UserDetail = () => {
  const { id } = useParams();
  return <UserDetailContent key={id} />;
};

export default UserDetail;
